import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

// Estimate (3 items) — only to pick downward-vs-upward flip near the viewport edge.
const MENU_HEIGHT_PX = 132;

interface JobsKebabMenuProps {
  onRename: () => void;
  onArchive: () => void;
  onDelete: () => void;
  ariaLabel?: string;
}

function KebabIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <circle cx="12" cy="5" r="2" />
      <circle cx="12" cy="12" r="2" />
      <circle cx="12" cy="19" r="2" />
    </svg>
  );
}

function PencilIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
    </svg>
  );
}

function ArchiveIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="4" width="18" height="4" rx="1" />
      <path d="M5 8v11a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V8" />
      <line x1="10" y1="12" x2="14" y2="12" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-2 14a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6" />
      <path d="M14 11v6" />
    </svg>
  );
}

export function JobsKebabMenu({
  onRename,
  onArchive,
  onDelete,
  ariaLabel = "Thread options",
}: JobsKebabMenuProps) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<{ top: number; right: number } | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    function onDocMouseDown(e: MouseEvent) {
      const target = e.target as Node;
      if (buttonRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    function dismiss() {
      setOpen(false);
    }
    document.addEventListener("mousedown", onDocMouseDown);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", dismiss, true);
    window.addEventListener("resize", dismiss);
    return () => {
      document.removeEventListener("mousedown", onDocMouseDown);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", dismiss, true);
      window.removeEventListener("resize", dismiss);
    };
  }, [open]);

  // stopPropagation invariant: parent row's onClick MUST NOT fire when the
  // operator interacts with the kebab menu. Both the trigger toggle AND
  // menu item clicks stop bubbling — otherwise opening the menu (or
  // selecting Delete) would also trigger the row's "open thread" handler.
  function handleItem(callback: () => void) {
    return (e: React.MouseEvent) => {
      e.stopPropagation();
      callback();
      setOpen(false);
    };
  }

  function toggle(e: React.MouseEvent) {
    e.stopPropagation();
    if (open) {
      setOpen(false);
      return;
    }
    const r = buttonRef.current?.getBoundingClientRect();
    if (r) {
      const below = r.bottom + 4;
      const flipUp = below + MENU_HEIGHT_PX > window.innerHeight;
      setCoords({
        top: flipUp ? r.top - MENU_HEIGHT_PX - 4 : below,
        right: window.innerWidth - r.right,
      });
    }
    setOpen(true);
  }

  return (
    <div>
      <button
        ref={buttonRef}
        type="button"
        aria-label={ariaLabel}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={toggle}
        className="flex h-6 w-6 items-center justify-center rounded-[var(--radius-pill)] text-[var(--text-secondary)] transition-colors duration-[var(--dur-fast)] ease-[var(--ease-out)] hover:bg-[var(--surface-sunken)] hover:text-[var(--text-primary)] focus-visible:outline-none focus-visible:shadow-[var(--elev-focus)]"
      >
        <KebabIcon />
      </button>
      {open &&
        coords &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            style={{ position: "fixed", top: coords.top, right: coords.right }}
            className="z-50 min-w-[140px] overflow-hidden rounded-[var(--radius-lg)] bg-[var(--surface-raised)] shadow-[var(--elev-whisper-lg)] ring-1 ring-[var(--border-default)]"
          >
            <button
              type="button"
              role="menuitem"
              onClick={handleItem(onRename)}
              className="type-body-sm flex w-full items-center gap-2 px-3 py-2 text-left text-[var(--text-primary)] transition-colors duration-[var(--dur-fast)] ease-[var(--ease-out)] hover:bg-[var(--surface-sunken)]"
            >
              <PencilIcon />
              Rename
            </button>
            <button
              type="button"
              role="menuitem"
              onClick={handleItem(onArchive)}
              className="type-body-sm flex w-full items-center gap-2 px-3 py-2 text-left text-[var(--text-primary)] transition-colors duration-[var(--dur-fast)] ease-[var(--ease-out)] hover:bg-[var(--surface-sunken)]"
            >
              <ArchiveIcon />
              Archive
            </button>
            <button
              type="button"
              role="menuitem"
              onClick={handleItem(onDelete)}
              className="type-body-sm flex w-full items-center gap-2 px-3 py-2 text-left text-[var(--status-error)] transition-colors duration-[var(--dur-fast)] ease-[var(--ease-out)] hover:bg-[var(--status-error-bg)]"
            >
              <TrashIcon />
              Delete
            </button>
          </div>,
          document.body,
        )}
    </div>
  );
}
